/* eslint-disable complexity */
// import { urinalysisTableSections } from "../../components/ObservationChart";
import { startCase } from "lodash";
import { AHC_DIRECTIVE, ALCOHOL_STATUS, DRUG_STATUS, EMPLOYMENT_STATUS, HEALTH_HIST_LABELS, HOME_STATUS, HOME_SUPPORT, SMOKE_STATUS } from "../../components/Patient/PatientInfoTabConsts";
import { SYMPTOM_LABELS } from "../../resources/constants";
import { EncounterPrintable } from "./types/obschart.interfaces";
import { camelCaseToTitleCase, capitalizeFirstLetter } from "./util";
import { formatUtc2HospitalDateDbTimestampFormat, momentHospitalTime } from "@/global";
import { findOptionFromSettings } from "@/util/MiscUtils";

type ComplaintCodeNode = {
  id: string;
  name: string;
  children?: ComplaintCodeNode[];
};

const findComplaintCodePath = (
  nodes: ComplaintCodeNode[],
  codeId: string,
  trail: string[] = [],
): string[] | null => {
  for (const node of nodes) {
    const nextTrail = [...trail, node.name];
    if (node.id === codeId) {
      return nextTrail;
    }
    if (node.children?.length) {
      const childPath = findComplaintCodePath(node.children, codeId, nextTrail);
      if (childPath) {
        return childPath;
      }
    }
  }
  return null;
};

export const resolveComplaintCodeFullPath = (complaintCode: string | undefined, settings?: any) => {
  const fallback = (complaintCode || "").trim();
  if (!fallback) return "Not Supplied";

  const complaintCodes = (settings as any)?.complaintCodes as ComplaintCodeNode[] | undefined;
  if (!Array.isArray(complaintCodes) || complaintCodes.length === 0) {
    return fallback;
  }

  const foundPath = findComplaintCodePath(complaintCodes, fallback);
  if (!foundPath || foundPath.length === 0) {
    return fallback;
  }

  return foundPath.join(" → ");
};

export const resolveTriageNurseName = (encounter: any) => {
  const firstName = (encounter?.triageNurse?.firstName || "").trim();
  const lastName = (encounter?.triageNurse?.lastName || "").trim();
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) {
    return fullName;
  }

  return "Not Supplied";
};

const formatCodeForDisplay = (value: string) => {
  return startCase((value || "").replace(/[-_]+/g, " "));
};

export const buildExpectsInformationRows = (encounter: any, settings?: any): [string, string][] => {
  const rows: [string, string][] = [];
  const visitDetails = (settings as any)?.visitDetails || {};

  const expectsNote = (encounter?.problem?.expects ?? "").trim();
  if (expectsNote) {
    rows.push(["Expects note", expectsNote]);
  }

  if (encounter?.admittingSpecialty) {
    rows.push(["Admitting Specialty", encounter.admittingSpecialty]);
  }

  if (encounter?.bedSlipSubmitted) {
    rows.push([
      "Bed Slip Submitted",
      encounter.bedSlipSubmitted === "yes" ? "Yes" : "No",
    ]);
  }

  if (encounter?.triageDirection) {
    const triageDirectionOptions = visitDetails?.triageDirectionOptions || [];
    const triageDirectionMapped = triageDirectionOptions.find(
      (option: any) => option?.code === encounter.triageDirection,
    );
    const triageDirectionFallback = formatCodeForDisplay(encounter.triageDirection || "");
    rows.push([
      "Triage Direction",
      triageDirectionMapped?.description || triageDirectionFallback,
    ]);
  }

  if (encounter?.patientOrigin) {
    const patientOriginOptions = visitDetails?.patientOriginOptions || [];
    const patientOriginMapped = patientOriginOptions.find(
      (option: any) => option?.code === encounter.patientOrigin,
    );
    const patientOriginFallback = formatCodeForDisplay(encounter.patientOrigin || "");
    rows.push([
      "Patient Origin",
      patientOriginMapped?.description || patientOriginFallback,
    ]);
  }

  if (encounter?.referrerDetails?.from) {
    rows.push(["Referrer From", encounter.referrerDetails.from]);
  }

  if (encounter?.referrerDetails?.contactName) {
    rows.push(["Referrer Contact Name", encounter.referrerDetails.contactName]);
  }

  if (encounter?.referrerDetails?.contactNumber) {
    rows.push(["Referrer Contact Number", encounter.referrerDetails.contactNumber]);
  }

  return rows;
};

/**
 * TODO pulled straight from `PatientInfoTab.tsx`. Put this logic in a shared place.
 */
const extractSocialHistory = (patient: any) => {

  const isAtLeastOneQuestionAnswered =
    patient?.employmentStatus ||
    patient?.alcoholConsumption ||
    patient?.homeStatus ||
    patient?.recreationalDrugs ||
    patient?.homeSupport ||
    patient?.tobaccoConsumption ||
    patient?.healthcareDirective;

  const socialHist = !isAtLeastOneQuestionAnswered
    ? []
    : [
      patient?.employmentStatus ? ["Employment", (EMPLOYMENT_STATUS as any)[patient.employmentStatus] || "No Response"] : null,
      patient?.alcoholConsumption ? ["Alcohol Consumption", (ALCOHOL_STATUS as any)[patient.alcoholConsumption] || "No Response"] : null,
      patient?.homeStatus ? ["Living Situation", (HOME_STATUS as any)[patient.homeStatus] || "No Response"] : null,
      patient?.recreationalDrugs ? ["Drug Status", (DRUG_STATUS as any)[patient.recreationalDrugs] || "No Response"] : null,
      patient?.homeSupport ? ["Home Support", (HOME_SUPPORT as any)[patient.homeSupport] || "No Response"] : null,
      patient?.tobaccoConsumption ? ["Smoke Status", (SMOKE_STATUS as any)[patient.tobaccoConsumption] || "No Response"] : null,
      patient?.healthcareDirective ? ["Health Care Directive", (AHC_DIRECTIVE as any)[patient.healthcareDirective] || "No Response"] : null,
    ].filter((x) => x !== null);

  return (socialHist);
};

const rhythmLong2RhythmAbbrev = (rhythmLong: string) => {

  switch (rhythmLong) {
    case "Regular":
      return ("Reg");
    case "Irregular":
      return ("Ireg");
    case "Paced":
      return ("Pace");
    case "SR - Sinus Rhythm":
      return ("SR");
    case "A Fib - Atrial Fibrillation":
      return ("AFib");
    case "A Flut - Atrial Flutter":
      return ("AFlt");
    case "1°HB - First-degree Heart Block":
      return ("1°HB");
    case "2°HB - Second-degree Heart Block":
      return ("2°HB");
    case "3°HB - Third-degree Heart Block":
      return ("3°HB");
    case "SVT - Supraventricular Tachycardia":
      return ("SVT");
    case "VF - Ventricular Fibrillation":
      return ("VF");
    case "VT - Ventricular Tachycardia":
      return ("VT");
    default:
      // we can just shorten this down to the first 3 characters, and clean away anything non alpha-numeric
      return ((rhythmLong || "").substring(0, 3).replace(/[^a-zA-Z0-9]/g, ""));
  }
};

/**
 * Marshals the data into a format that can be used by the printable encounter component.
 * We convert any UTC dates into Perth time here.
 */
export const encounter2PrintableEncounter = (encounter: any, settings?: any): EncounterPrintable => {

  const convertYyyyMmDdToDdMmYyyy = (date: string) => {

    // first test to see if the date is in the expected format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateRegex.test(date || "")) {
      const [yyyy, mm, dd] = date.split("-");
      return `${dd}/${mm}/${yyyy}`;
    }

    return (date);
  };

  const calculateAgeAtReference = (date: string, referenceMs: number) => {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date || "") || !Number.isFinite(referenceMs)) {
      return null;
    }

    const [yyyy, mm, dd] = date.split("-").map((part) => Number(part));
    const dob = new Date(Date.UTC(yyyy, mm - 1, dd));
    if (
      dob.getUTCFullYear() !== yyyy ||
      dob.getUTCMonth() !== (mm - 1) ||
      dob.getUTCDate() !== dd
    ) {
      return null;
    }

    const ref = new Date(referenceMs);
    if (Number.isNaN(ref.getTime())) {
      return null;
    }

    let totalMonths =
      (ref.getUTCFullYear() - dob.getUTCFullYear()) * 12 +
      (ref.getUTCMonth() - dob.getUTCMonth());

    if (ref.getUTCDate() < dob.getUTCDate()) {
      totalMonths -= 1;
    }

    if (totalMonths < 0) {
      return null;
    }

    return {
      months: totalMonths,
      years: Math.floor(totalMonths / 12),
    };
  };

  const observations = (encounter.observations || []).filter((obs: any) => (obs.invalid ?? false) !== true);
  observations.forEach((obs: any) => {
    if (obs.rhythm) {
      obs.rhythm = rhythmLong2RhythmAbbrev(obs.rhythm);
    }
  });
  // for each of the observations, change the created date into perth time
  observations.forEach((obs: any) => {
    // obs.created = new Date(obs.created).toLocaleString("en-AU", { timeZone: "Australia/Perth" });
    obs.created = formatUtc2HospitalDateDbTimestampFormat(obs.created);
  });
  // and also convert dates on the notes
  encounter?.notes?.forEach((note: any) => {
    note.created = formatUtc2HospitalDateDbTimestampFormat(note.created);
  });
  // and also convert the creation date on the encounter
  encounter.created = formatUtc2HospitalDateDbTimestampFormat(encounter.created);

  const patientFirstName = encounter?.patient?.firstName || "";
  const patientLastName = encounter?.patient?.lastName || "";
  const patientUmrn = encounter?.patient?.umrn || "URN Unavailable";
  const patientPhone = encounter?.patient?.phone || "";
  const patientEmail = encounter?.patient?.email || "";
  const patientFullName = `${patientFirstName} ${patientLastName}`.trim();
  const patientDob = convertYyyyMmDdToDdMmYyyy(encounter?.patient?.dateOfBirth || "");
  const patientSexCode = encounter?.patient?.sex || "";
  const sexOptions = (settings?.patientDetails?.sexOptions || []) as Array<{ code?: string; description?: string }>;
  const patientSex = findOptionFromSettings(sexOptions, patientSexCode) || patientSexCode;
  const patientGenderCode = encounter?.patient?.gender || "";
  const genderOptions = (settings?.patientDetails?.genderOptions || []) as Array<{ code?: string; description?: string }>;
  const patientGender = findOptionFromSettings(genderOptions, patientGenderCode) || patientGenderCode;

  const encounterStartRaw = encounter?.arrived || encounter?.triaged || encounter?.created || Date.now();
  const patientAge = calculateAgeAtReference(encounter?.patient?.dateOfBirth || "", Number(encounterStartRaw));
  const ageDisplay = (() => {
    if (!patientAge) {
      return "Unknown";
    }
    if (patientAge.months < 24) {
      return `${patientAge.months} month${patientAge.months === 1 ? "" : "s"}`;
    }
    return `${patientAge.years} year${patientAge.years === 1 ? "" : "s"}`;
  })();
  const sexSuffix = patientSex ? ` (${patientSex.toLowerCase()})` : '';
  const ageSexDisplay = `${ageDisplay}${sexSuffix}`
  const patientDescription = `${ageDisplay} old ${patientSex.toLowerCase()}`.trim();

  const encounterStartMoment = momentHospitalTime(encounterStartRaw);
  const encounterStartDate = encounterStartMoment.isValid()
    ? encounterStartMoment.format("DD/MM/YYYY")
    : "Not Supplied";
  const encounterStartTime = encounterStartMoment.isValid()
    ? encounterStartMoment.format("hh:mm A")
    : "Not Supplied";
  const encounterStart = encounterStartMoment.isValid()
    ? encounterStartMoment.format("DD/MM/YYYY hh:mm A")
    : "Not Supplied";

  const txtNotSupplied = "Not Supplied";
  const triageNurseName = resolveTriageNurseName(encounter);
  const locationDisplay = (() => {
    const location = encounter?.location;
    if (!location) return txtNotSupplied;
    if (typeof location === "string") return location;
    return location.shortName || location.name || location.id || txtNotSupplied;
  })();
  const modeOfArrivalOptions = settings?.visitDetails?.modeOfArrivalOptions || [];
  const modeOfArrivalMapped = modeOfArrivalOptions.find(
    (option: any) => option?.code === encounter?.modeOfArrival,
  );
  const arrivalModeFallback = formatCodeForDisplay(encounter?.modeOfArrival || "");
  const arrivalModeDisplay = modeOfArrivalMapped?.description || arrivalModeFallback || txtNotSupplied;
  const complaintCodeFullPath = resolveComplaintCodeFullPath(encounter?.complaintCode, settings);
  const expectsInformation = buildExpectsInformationRows(encounter, settings);

  // note: the additional questions used to be on the patient object, but now they're on the encounter object, but
  //       to handle any hold encounters we need to handle both scenarios
  const additionalRawFallsRisk = encounter?.fallRisk || encounter?.patient?.fallRisk || {};
  const additionalRawHealthHistory = encounter?.healthHistory || encounter?.patient?.healthHistory || [];
  const additionalRawPreOperations = encounter?.previousOperations || encounter?.patient?.previousOperations || [];
  const regularMedsRawValue = encounter?.patient?.regularMeds || encounter?.regularMeds || "";

  const fallsRisk = (Object.entries(additionalRawFallsRisk)).map(([k, v]) => ([camelCaseToTitleCase(k), v ? "Yes" : "No"]));
  // allergies are always stored on the patient object
  const allergies = (encounter?.patient?.allergies || []).map((allergy: any) => ([capitalizeFirstLetter(allergy.description), allergy.severity ? capitalizeFirstLetter(allergy.severity) : ""]));

  const regularMeds = (() => {
    switch (regularMedsRawValue.toLowerCase()) {
      case "no":
        return ("No");
      case "yes":
        return ("Yes");
      case "morethan3":
        return ("Yes, More than 3");
      default:
        return "Unknown";
    }
  })();

  /**
   * This is the only one we do a little differently
   * so we wind up with something like:
   * ```
   * pastMedicalHistory: [
   *   "Immunological: anaemia",
   *   "Cardiovascular: hypertension",
   *   "Endocrine: diabetes",
   * ],
   * ```
   * Rather than the usual:
   * ```
   * pastMedicalHistory: [
   *  ["Immunological", "anaemia"],
   *  ["Cardiovascular", "hypertension"],
   *  ["Endocrine", "diabetes"],
   * ],
   */
  const pastMedicalHistory = (additionalRawHealthHistory).map(({ type, details }: { type: string, details: string }) => {

    if (type && details) {
      // const detailsCleaned = (details || "").replace(/[-_]/g, " ").trim();
      // const typeCleaned = camelCaseToTitleCase((type || "").replace(/[-_]/g, " "));
      const typeRaw = type || "";
      const typeDefault = startCase(typeRaw);
      const typeCleaned = (HEALTH_HIST_LABELS as any)[typeRaw] || typeDefault;
      const detailsCleaned = (details || "").trim().split(", ").map((it) => startCase(it)).join(", ");

      let desc = typeCleaned;
      if (detailsCleaned) {
        desc += `: ${detailsCleaned}`;
      }

      return (desc.trim());
    }

    return (null);
  }).filter((desc: string | null) => desc !== null);

  const surgicalHistory = [];
  for (const previousSurgery of (additionalRawPreOperations)) {

    const { type, when } = previousSurgery;
    if (type && when) {
      const convertedWhen = (() => {
        switch (when.toLowerCase()) {
          case "lt1month":
            return ("Less than 1 month ago");
          case "lt6months":
            return ("1 - 6 months ago");
          case "gt6months":
            return ("More than 6 months ago");
          default:
            return ("Unknown");
        }
      })();

      surgicalHistory.push([camelCaseToTitleCase(type), convertedWhen]);
    }
  }

  const testResults = observations.filter((obs: any) => Object.keys(obs).includes("urinalysis"));
  // TODO these aren't actual data keys...revisit this
  // const testResultKeyOrderUrinalysis = urinalysisTableSections.map((it) => it.dataKey);
  const testResultKeyOrderUrinalysis = [
    "pregnancy",
    "abnormalities",
    "distream",
    "leukocytes",
    "nitrite",
    "urobilinogen",
    "protein",
    "ph",
    "blood",
    "specificGravity",
    "ketone",
    "bilirubin",
    "glucose",
  ];
  // let's additionally order items in testResults.urinalysis according to the order in testResultKeyOrder
  testResults.forEach((testResult: any) => {
    const urinalysis = testResult.urinalysis;
    if (urinalysis) {
      const orderedUrinalysis = {};
      testResultKeyOrderUrinalysis.forEach((key) => {
        if (urinalysis[key]) {
          (orderedUrinalysis as any)[key] = urinalysis[key];
        }
      });
      // replace the original urinalysis with the ordered one, and put any missing items at the end
      testResult.urinalysis = {
        ...orderedUrinalysis,
        ...urinalysis,
      };
    }
  });

  const neuroObservations = [];
  for (const obs of observations) {
    if (obs.neuro) {
      neuroObservations.push(obs);
    }
  }

  // Collect any observations that include ventilator data with at least one value present
  const ventilatorObservations = [] as any[];
  for (const obs of observations) {
    const v = obs?.ventilator;
    if (v && typeof v === "object") {
      const hasValue = Object.values(v).some((val) => val !== undefined && val !== null && val !== "");
      if (hasValue) {
        ventilatorObservations.push(obs);
      }
    }
  }

  // Define the valid keys that should appear on the NEWS chart
  const validNewsChartKeys = [
    "respirations",
    "o2Sats",
    "o2Flow",
    "heartRate",
    "bloodPressure",
    "temperature",
    "consciousness",
    "rhythm",
    "bloodGlucose",
    "bloodAlcohol",
    "painScore"
  ];

  // Filter out observations that don't contain any valid NEWS chart data
  // This includes observations that are only urinalysis, only ventilator, only neuro, etc.
  const obsFortheNewsChart = observations.filter((obs: any) => {
    // Extract metadata keys that we always exclude from the chart
    const { created, createdBy, createdByName, createdByRole, id, lastModified, neuro, urinalysis, ventilator, ...rest } = obs;

    // Check if there are any valid NEWS chart keys in the remaining data
    const hasValidNewsChartData = validNewsChartKeys.some(key => rest[key] !== undefined);

    // Special handling for riskScore - only count it if it's not incomplete
    const hasValidRiskScore = obs.riskScore && obs.riskScore.score !== undefined && !obs.riskScore.incomplete;

    return hasValidNewsChartData || hasValidRiskScore;
  }).map((obs: any) => {
    // Remove neuro data from observations that go to the NEWS chart
    const { neuro, ...rest } = obs;
    return rest;
  });


  return ({
    title1: "Emergency Department",
    title2: "Visit Record",
    observations: obsFortheNewsChart,
    neuroObservations,
    ventilatorObservations,
    testResults,
    notes: encounter.notes,
    provider: {
      // TODO this will change when delegation is implemented
      email: patientEmail || txtNotSupplied,
      fullName: patientFullName || txtNotSupplied,
      phone: patientPhone || txtNotSupplied,
    },
    patient: {
      fullName: patientFullName,
      surname: patientLastName,
      forename: patientFirstName,
      dob: patientDob,
      sex: patientSex,
      gender: patientGender,
      uri: patientUmrn,
    },
    encounter: {
      patientDescription,
      ageSexDisplay,
      encounterStart,
      triageNurseName,
      locationDisplay,
      arrivalModeDisplay,
      complaintCodeFullPath,
      presentingProblem: encounter?.problem?.patient || txtNotSupplied,
      presentingProblemTriageNurse: encounter?.problem?.triage || txtNotSupplied,
      nursingAssessment: encounter?.problem?.nursingAssessment || txtNotSupplied,
      expectsInformation,
      ats: `${encounter?.atsScore || "-"}`,
      dateStart: encounterStartDate,
      timeStart: encounterStartTime,
      photos: encounter.photos || [],
      painScore: `${encounter?.pain ?? "-"}`,
      frailtyScore: `${encounter?.frailty ?? "-"}`,

      screeningInfectionRisks: (encounter?.infectiousSymptoms || ["No symptoms"]).map((symptom: string) => {

        const defaultText = camelCaseToTitleCase(symptom);
        const mappedText = (SYMPTOM_LABELS as any)[symptom];

        return (mappedText || defaultText);
      }),

      fallsRisk: (fallsRisk as any) || [],
      allergies: (allergies as any) || [],
      medications: regularMeds,
      pastMedicalHistory: pastMedicalHistory,
      surgicalHistory: surgicalHistory as any,

      socialHistory: extractSocialHistory({
        ...(encounter?.patient) || {},
        ...encounter || {}, // just accommodating that these fields were moved out of the patient object and into the encapsulating encounter object
      }) as any,
    },
  });
};
